import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Slider,
  InputAdornment,
  Divider,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import SourceIcon from '@mui/icons-material/Source';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../services/api';

interface LeadTag {
  id: number;
  name: string;
  color: string;
}

interface Lead {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  position?: string | null;
  status: string;
  status_display?: string;
  source: string | null;
  source_display?: string;
  priority?: string;
  priority_display?: string;
  notes: string | null;
  probability?: number | null;
  estimated_value?: number | null;
  tags?: LeadTag[];
  created_at?: string;
  first_contact_date?: string | null;
  last_contact_date?: string | null;
  follow_up_date?: string | null;
}

interface LeadEditModalProps {
  open: boolean;
  onClose: () => void;
  lead: Lead | null;
  onSave: (updatedLead: Lead) => void;
}

const SOURCE_OPTIONS = [
  { value: 'website', label: 'Website' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'referral', label: 'Indicação' },
  { value: 'event', label: 'Evento' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Telefone' },
  { value: 'other', label: 'Outro' },
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'Novo' },
  { value: 'contacted', label: 'Contatado' },
  { value: 'qualified', label: 'Qualificado' },
  { value: 'proposal', label: 'Proposta Enviada' },
  { value: 'negotiation', label: 'Negociação' },
  { value: 'won', label: 'Ganho' },
  { value: 'lost', label: 'Perdido' },
  { value: 'archived', label: 'Arquivado' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const LeadEditModal: React.FC<LeadEditModalProps> = ({ open, onClose, lead, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    source: 'website',
    status: 'new',
    priority: 'medium',
    notes: '',
    probability: 50,
    estimated_value: ''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        company: lead.company || '',
        position: lead.position || '',
        source: lead.source || 'website',
        status: lead.status || 'new',
        priority: lead.priority || 'medium',
        notes: lead.notes || '',
        probability: lead.probability || 50,
        estimated_value: lead.estimated_value?.toString() || ''
      });
    }
  }, [lead]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!lead) return;

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      const dataToSend: any = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        company: formData.company || null,
        position: formData.position || null,
        source: formData.source,
        status: formData.status,
        priority: formData.priority,
        notes: formData.notes || null,
        probability: formData.probability,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null
      };

      const response = await api.patch(`/leads/leads/${lead.id}/`, dataToSend);
      onSave(response.data);
      onClose();
    } catch (error) {
      console.error('Error saving lead:', error);
    } finally {
      setSaving(false);
    }
  };

  // Helper function to get lead temperature/score
  const getLeadTemperature = (probability: number) => {
    if (probability >= 70) return { label: 'Quente', color: '#ef4444', icon: WhatshotIcon, bg: 'rgba(239,68,68,0.15)' };
    if (probability >= 40) return { label: 'Morno', color: '#f59e0b', icon: WbSunnyIcon, bg: 'rgba(245,158,11,0.15)' };
    return { label: 'Frio', color: '#3b82f6', icon: AcUnitIcon, bg: 'rgba(59,130,246,0.15)' };
  };

  const formatTimeAgo = (dateString: string | null | undefined) => {
    if (!dateString) return 'Nunca';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  if (!lead) return null;

  const temp = getLeadTemperature(formData.probability);
  const TempIcon = temp.icon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <PersonIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
              Editar Lead
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {lead.name}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Quick Info Cards - Same as removed from sidebar */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 1.5 }}>
            Indicadores
          </Typography>

          <Grid container spacing={2}>
            {/* Score/Temperature Card */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                background: temp.bg,
                border: `1px solid ${temp.color}30`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TempIcon sx={{ fontSize: 28, color: temp.color }} />
                  <Box>
                    <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Temperatura</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: temp.color }}>
                      {temp.label}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Score</Typography>
                  <Typography sx={{ fontSize: 24, fontWeight: 800, color: temp.color }}>
                    {formData.probability}%
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Info Grid */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, height: '100%' }}>
                {/* Origem */}
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  background: 'rgba(99,102,241,0.08)',
                  border: '1px solid rgba(99,102,241,0.15)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                    <SourceIcon sx={{ fontSize: 14, color: '#6366f1' }} />
                    <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Origem</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                    {SOURCE_OPTIONS.find(s => s.value === formData.source)?.label || formData.source}
                  </Typography>
                </Box>

                {/* Tempo no Funil */}
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  background: 'rgba(139,92,246,0.08)',
                  border: '1px solid rgba(139,92,246,0.15)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />
                    <Typography sx={{ fontSize: 11, color: '#6b7280' }}>No Funil</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                    {formatTimeAgo(lead.created_at)}
                  </Typography>
                </Box>

                {/* Último Contato */}
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  background: 'rgba(34,197,94,0.08)',
                  border: '1px solid rgba(34,197,94,0.15)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                    <HistoryIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                    <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Últ. Contato</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                    {formatTimeAgo(lead.last_contact_date)}
                  </Typography>
                </Box>

                {/* Status */}
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  background: 'rgba(245,158,11,0.08)',
                  border: '1px solid rgba(245,158,11,0.15)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                    <TrendingUpIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                    <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Status</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                    {STATUS_OPTIONS.find(s => s.value === formData.status)?.label || formData.status}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Score Slider */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 1 }}>
            Score de Conversão
          </Typography>
          <Box sx={{ px: 1 }}>
            <Slider
              value={formData.probability}
              onChange={(_, value) => handleChange('probability', value)}
              valueLabelDisplay="on"
              valueLabelFormat={(value) => `${value}%`}
              min={0}
              max={100}
              sx={{
                color: temp.color,
                '& .MuiSlider-thumb': {
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: `0 0 0 8px ${temp.color}20`
                  }
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -1 }}>
              <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>Frio</Typography>
              <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>Morno</Typography>
              <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>Quente</Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Basic Info */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937' }}>
            Informações Básicas
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome"
                fullWidth
                size="small"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                fullWidth
                size="small"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Telefone"
                fullWidth
                size="small"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Empresa"
                fullWidth
                size="small"
                value={formData.company}
                onChange={(e) => handleChange('company', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>

          {/* Lead Details */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mt: 1 }}>
            Detalhes do Lead
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Origem</InputLabel>
                <Select
                  value={formData.source}
                  label="Origem"
                  onChange={(e) => handleChange('source', e.target.value)}
                >
                  {SOURCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={formData.priority}
                  label="Prioridade"
                  onChange={(e) => handleChange('priority', e.target.value)}
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Valor Estimado (R$)"
                fullWidth
                size="small"
                type="number"
                value={formData.estimated_value}
                onChange={(e) => handleChange('estimated_value', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Cargo"
                fullWidth
                size="small"
                value={formData.position}
                onChange={(e) => handleChange('position', e.target.value)}
              />
            </Grid>
          </Grid>

          {/* Notes */}
          <TextField
            label="Notas"
            fullWidth
            multiline
            rows={3}
            size="small"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Adicione observações sobre o lead..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
            }
          }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadEditModal;
